import { AppService } from '../../core/services/app.service';
import { CsrdResultsDto, HnnResultsDto, NmdCalculationResponseDto, PrintResultDto, ProjectDto, QciResultsDto, WlcResultsDto } from '../../core/services/GPRMV1Service.Generated';
import { ValuePerPhaseModel } from '../../shared/models/value-per-phase.model';
import { Component, Input, AfterViewInit, OnDestroy, OnChanges } from '@angular/core';
import { CombinationBuildingPrintDto } from './print-combination-building-details/print-combination-building-details.component';
import { Subject, takeUntil } from 'rxjs';
import { FeatureManagementService } from '../../core/services/feature-management.service';
import { BuildingUseFunctionNamesById } from '../../shared/enums/building-use-function-names.enum';

@Component({
    selector: 'app-print-page-landscape',
    templateUrl: './print-page-landscape.component.html',
    styleUrls: ['./print-page-landscape.component.scss'],
    standalone: false
})
export class PrintPageLandscapeComponent implements AfterViewInit, OnDestroy, OnChanges {
// Constants mapping to ParameterTypeEnum values from backend
private static readonly PARAM_BUILDING_USE_FUNCTION = 'Gebruiksfunctie';
private static readonly PARAM_BVO = 'grossFloorArea';
private static readonly PARAM_GBO = 'usableFloorArea';
private static readonly PARAM_NUMBER_OF_UNITS = 'numberOfUnits';
private static readonly PARAM_FUNCTION_NEW_OR_CURRENT = 'Functies';
private static readonly PARAM_FUNCTION_AFTER = 'Functies na ingreep';
private static readonly PARAM_COMMON_AREA_NEW_OR_CURRENT = 'Gemeenschappelijke ruimte';
private static readonly PARAM_COMMON_AREA_AFTER = 'Gemeenschappelijke ruimte na ingreep';
private static readonly PARAM_FACADE_SURFACE_NEW_OR_CURRENT = 'Geveloppervlakte';
private static readonly PARAM_FACADE_SURFACE_AFTER = 'Geveloppervlakte na ingreep';
private static readonly PARAM_COMPACTNESS_NEW_OR_CURRENT = 'Compactheid';
private static readonly PARAM_MPG_AMBITION_NEW_OR_CURRENT = 'Mpg ambitie';
private static readonly PARAM_MPG_LIMIT_A2_NEW_OR_CURRENT = 'Mpg Limit';
    private static readonly PARAM_MPG_LIMIT_EASED_A2_NEW_OR_CURRENT = 'Mpg Limit Eased';
private static readonly PARAM_CORRECTION_FACTOR_NEW_OR_CURRENT = 'Correctiefactor';

// Provide safe defaults so the template can render in unit tests
// Use the generated DTO types and provide minimal defaults so the
// template can safely read properties during tests.
@Input() printData: PrintResultDto = ({
    mainCharacteristics: [],
    createdByUserName: '',
    organisationName: '',
    materialsAppVersionNumber: 0,
    lastNmdProductUpdateDate: undefined
} as unknown) as PrintResultDto;
    @Input() calculationName: string = '';
    @Input() projectCharacteristics: ProjectDto = ({
        id: undefined,
        streetName: '',
        houseNumber: undefined,
        houseNumberAddition: undefined,
        houseLetter: '',
        postalCode: '',
        town: '',
        client: '',
        architect: '',
        buildingPermitDate: undefined,
        phaseId: undefined,
        statusId: undefined,
        purposeValue: undefined
    } as unknown) as ProjectDto;
    @Input() isProjectPhaseUIEnabled: boolean = false;
    @Input() isExistingBuildingCal: boolean = false;
    readonly printDate: Date = new Date();
    @Input() elementResultTitle: string;
    @Input() calculationResults: NmdCalculationResponseDto | null = null;
    @Input() calculationResultsAfter: NmdCalculationResponseDto | null = null;
    @Input() mpgResultTitle: string;
    @Input() selectedDataSet: number;
    @Input() mpgResultTitlePerMainElementTitle: string;
    @Input() envirnmentalCategoryTitle: string;
    @Input() environmentEffects: ValuePerPhaseModel[];
    @Input() environmentEffectsAfter: ValuePerPhaseModel[];
    @Input() selectedsubmenu: string;
    @Input() chartOption: any;
    @Input() currentMainElements: any[];
    @Input() afterMainElements: any[];
    @Input() codes: {};
    @Input() tableColumns: string[];
    @Input() qciResultsDto: QciResultsDto | null = null;
    @Input() hnnResultsDto: HnnResultsDto | null = null;
    @Input() csrdResultsDto: CsrdResultsDto | null = null;
    @Input() wlcResultsDto: WlcResultsDto | null = null;
    @Input() mpg: number;
    @Input() parisProofIndicator: number;
    @Input() totalUsedProducts: number;
    @Input() constructionStoredCarbon: number;
    @Input() isShowIndicatorPage: boolean = false;

    isShowCombinationBuildingFeature: boolean = false;
    combinationBuildingDetails: CombinationBuildingPrintDto | null = null;
    parameterGroupsCache: Map<any, any[][]> = new Map();
    protected _onDestroy: Subject<void> = new Subject<void>();
    constructor(private _appService: AppService, private _featureService: FeatureManagementService) {
    }

    ngAfterViewInit(): void {

        this.getCombinationBuildingFeatureFlag();

        if (this._appService.isPrintPreview()) {
            try {
                document.body.classList.add('print-preview', 'popup-preview');
                document.documentElement.classList.add('print-preview', 'popup-preview');
            } catch (e) { /* ignore */ }
        }
    }

    ngOnChanges(): void {
        this.combinationBuildingDetails = this.buildCombinationBuildingDetails();
        this.buildParameterGroupsCache();
    }

    public get isCombinationBuildingSelected(): boolean {
        return this.combinationBuildingDetails !== null;
    }

    get preparedBy(): string {
        return (this.printData && this.printData.createdByUserName);
    }

    get organisationName(): string {
        return (this.printData && this.printData.organisationName);
    }


    ngOnDestroy(): void {
        document.body.classList.remove('print-preview', 'popup-preview');
        document.documentElement.classList.remove('print-preview', 'popup-preview');
    }

    /** Returns true when the value is a JSON array or object string (should be hidden from the print view). */
    isJsonValue(value: string | undefined): boolean {
        if (!value) return false;
        const trimmed = value.trim();
        return trimmed.startsWith('[') || trimmed.startsWith('{');
    }

    /**
     * Builds a cache of parameter groups for all main characteristics.
     * This is called once in ngOnChanges to avoid repeated calculations during change detection.
     */
    private buildParameterGroupsCache(): void {
        this.parameterGroupsCache.clear();
        
        if (!this.printData?.mainCharacteristics) {
            return;
        }

        this.printData.mainCharacteristics.forEach(characteristic => {
            if (characteristic.parameters) {
                const groups = this.computeParameterGroups(characteristic.parameters);
                this.parameterGroupsCache.set(characteristic, groups);
            }
        });
    }

    /**
     * Gets the cached parameter groups for a main characteristic.
     * @param characteristic The main characteristic to get parameter groups for
     * @returns Array of parameter groups, each containing up to 4 parameters
     */
    getParameterGroups(characteristic: any): any[][] {
        return this.parameterGroupsCache.get(characteristic) || [];
    }

    /**
     * Computes parameter groups from raw parameters.
     * Splits parameters into groups of 4 (excluding JSON values) for display in columns.
     * Each group will be rendered as a separate column with a divider (except the first).
     * For combination buildings, filters out combination-specific parameters that are shown on the dedicated page.
     * @param parameters Array of parameters to group
     * @returns Array of parameter groups, each containing up to 4 parameters
     */
    private computeParameterGroups(parameters: any[]): any[][] {
        if (!parameters || !parameters.length) {
            return [];
        }

        // Filter out JSON values first
        let validParameters = parameters.filter(param => !this.isJsonValue(param.value));

        // If this is a combination building, filter out the combination-specific parameters
        // These parameters (6-13) are displayed on the dedicated combination building page
        const commonExcludedParams = [
            PrintPageLandscapeComponent.PARAM_MPG_LIMIT_EASED_A2_NEW_OR_CURRENT,
            PrintPageLandscapeComponent.PARAM_CORRECTION_FACTOR_NEW_OR_CURRENT
        ];

        const combinationOnlyExcludedParams = [
            PrintPageLandscapeComponent.PARAM_FACADE_SURFACE_NEW_OR_CURRENT,
            PrintPageLandscapeComponent.PARAM_MPG_AMBITION_NEW_OR_CURRENT,
            PrintPageLandscapeComponent.PARAM_COMPACTNESS_NEW_OR_CURRENT,
            PrintPageLandscapeComponent.PARAM_FUNCTION_NEW_OR_CURRENT,
            PrintPageLandscapeComponent.PARAM_COMMON_AREA_NEW_OR_CURRENT,
            PrintPageLandscapeComponent.PARAM_MPG_LIMIT_A2_NEW_OR_CURRENT
        ];

        const excludedParamNames = new Set(
            [
                ...commonExcludedParams,
                ...(this.isCombinationBuildingSelected ? combinationOnlyExcludedParams : [])
            ].map(name => this.normalizeKey(name))
        );

        validParameters = validParameters.filter(param =>
            !excludedParamNames.has(this.normalizeKey(param.name))
        );

        // Split into groups of 4
        const groups: any[][] = [];
        const groupSize = 4;

        for (let i = 0; i < validParameters.length; i += groupSize) {
            groups.push(validParameters.slice(i, i + groupSize));
        }

        return groups;
    }

    /**
     * Checks if building characteristics has 3 or more columns.
     * Returns true if any main characteristic has 9 or more parameters (which creates 3+ columns).
     * @returns True if 3 or more columns are needed, false otherwise
     */
    hasThreeOrMoreColumns(): boolean {
      

        if (!this.printData?.mainCharacteristics) {
            return false;
        }

        return this.printData.mainCharacteristics.some(characteristic => {
            const paramGroups = this.parameterGroupsCache.get(characteristic);
            return paramGroups && paramGroups.length >= 3;
        });
    }


    private buildCombinationBuildingDetails(): CombinationBuildingPrintDto | null {
        const characteristics = this.printData?.mainCharacteristics ?? [];

        const combinationBuilding = characteristics.find(x =>
            this.normalize(this.getParameterValue(x, [PrintPageLandscapeComponent.PARAM_BUILDING_USE_FUNCTION])) === 'combinatiegebouw'
        );

        if (!combinationBuilding) {
            return null;
        }

        const parsedFunctions = this.getCombinationFunctions(combinationBuilding);
        const functions = parsedFunctions.length
            ? parsedFunctions
            : characteristics
                .filter(x => x !== combinationBuilding)
                .filter(x => !!this.getParameterValue(x, [PrintPageLandscapeComponent.PARAM_BUILDING_USE_FUNCTION]))
                .map(x => this.mapFunction(x))
                .filter(x => x.name && this.normalize(x.name) !== 'combinatiegebouw');

        return {
            functions,
            sharedSpaces: this.getSharedSpaces(combinationBuilding, functions),
            facadeSurface: {
                surface: this.readNumber(combinationBuilding, [
                    PrintPageLandscapeComponent.PARAM_FACADE_SURFACE_NEW_OR_CURRENT
                ], 0),
                compactness: this.readNumber(combinationBuilding, [
                    PrintPageLandscapeComponent.PARAM_COMPACTNESS_NEW_OR_CURRENT
                ], 0),
                mpgAmbition: this.readNumber(combinationBuilding, [
                    'MPG ambitie',
                    PrintPageLandscapeComponent.PARAM_MPG_AMBITION_NEW_OR_CURRENT
                ], 0),
                // Note: Search for mpgLimitEasedA2 first as it's more specific (longer string)
                // to avoid fuzzy matching issues where "Mpg Limit" matches "Mpg Limit Eased"
                mpgLimitEasedA2: this.readNumber(combinationBuilding, [
                    PrintPageLandscapeComponent.PARAM_MPG_LIMIT_EASED_A2_NEW_OR_CURRENT
                ], 0),
                mpgLimitA2: this.readNumber(combinationBuilding, [
                    PrintPageLandscapeComponent.PARAM_MPG_LIMIT_A2_NEW_OR_CURRENT
                ], 0),
                correctionFactor: this.readNumber(combinationBuilding, [
                    PrintPageLandscapeComponent.PARAM_CORRECTION_FACTOR_NEW_OR_CURRENT
                ], 0)
            }
        };
    }

    private getCombinationFunctions(source: any): any[] {
        const rows = this.getJsonArray(source, [
            PrintPageLandscapeComponent.PARAM_FUNCTION_NEW_OR_CURRENT
        ]);

        return rows.map((row: any) => {
            const referenceNumber = this.readNumber(row, ['referenceNumber'], 0);
            const buildingUseFunctionId = this.readNumber(row, ['buildingUseFunctionId'], 0);
            const units = this.readNumber(row, [
                PrintPageLandscapeComponent.PARAM_NUMBER_OF_UNITS
            ], 1);
            const bvo = this.readNumber(row, [
                PrintPageLandscapeComponent.PARAM_BVO
            ], 0);
            const go = this.readNumber(row, [
                PrintPageLandscapeComponent.PARAM_GBO
            ], 0);
            const explicitName = this.readText(row, [
                PrintPageLandscapeComponent.PARAM_BUILDING_USE_FUNCTION.toLowerCase()
            ]);

            // Use mpgLimitEasedA2 if it exists and is not empty, otherwise use mpgLimitA2
            const mpgLimitEasedA2 = this.readNumber(row, ['mpgLimitEasedA2'], 0);
            const mpgLimitA2 = this.readNumber(row, ['mpgLimitA2'], 0);
            const mpgRequirement = (mpgLimitEasedA2 !== 0) ? mpgLimitEasedA2 : mpgLimitA2;

            return {
                referenceNumber,
                name: explicitName || BuildingUseFunctionNamesById[buildingUseFunctionId] || `Functie ${buildingUseFunctionId || referenceNumber}`,
                units,
                bvo,
                go,
                bvoTotal: this.readNumber(row, ['totalGrossFloorArea'],0),
                goTotal: this.readNumber(row, ['totalUsableFloorArea'],0),
                mpgRequirement: mpgRequirement
            };
        });
    }

    private mapFunction(source: any) {
        const units = this.readNumber(source, [
            PrintPageLandscapeComponent.PARAM_NUMBER_OF_UNITS
        ], 1);
        const bvo = this.readNumber(source, [
            PrintPageLandscapeComponent.PARAM_BVO
        ], 0);
        const go = this.readNumber(source, [
            PrintPageLandscapeComponent.PARAM_GBO
        ], 0);

        // Use mpgLimitEasedA2 if it exists and is not empty, otherwise use mpgLimitA2
        const mpgLimitEasedA2 = this.readNumber(source, ['mpgLimitEasedA2'], 0);
        const mpgLimitA2 = this.readNumber(source, ['mpgLimitA2'], 0);
        const mpgRequirement = (mpgLimitEasedA2 !== 0) ? mpgLimitEasedA2 : mpgLimitA2;

        return {
            name: this.getParameterValue(source, [PrintPageLandscapeComponent.PARAM_BUILDING_USE_FUNCTION]) ?? '',
            units,
            bvo,
            go,
            bvoTotal: this.readNumber(source, ['totalGrossFloorArea'], 0),
            goTotal: this.readNumber(source, ['totalUsableFloorArea'], 0),
            mpgRequirement: mpgRequirement
        };
    }

    private getSharedSpaces(source: any, functions: any[] = []) {
        const rows = this.getJsonArray(source, [
            PrintPageLandscapeComponent.PARAM_COMMON_AREA_NEW_OR_CURRENT
        ]);

        const functionLookup = new Map<number, any>(functions
            .filter(x => Number.isFinite(x.referenceNumber))
            .map(x => [x.referenceNumber, x]));

        return rows.map(row => {
            const referenceNumber = this.readNumber(row, ['functionReferenceNumber'], 0);
            const buildingUseFunctionId = this.readNumber(row, ['buildingUseFunctionId'], 0);
            const derivedAllocation = functionLookup.get(referenceNumber)?.name
                || BuildingUseFunctionNamesById[buildingUseFunctionId]
                || '';

            return {
                bvo: this.readNumber(row, ['grossFloorArea'], 0),
                gbo: this.readNumber(row, ['usableFloorArea'], 0),
                allocation:   derivedAllocation,
                functionReferenceNumber: referenceNumber
            };
        });
    }

    private getJsonArray(source: any, names: string[]): any[] {
        const raw = this.readRaw(source, names);

        if (!raw || typeof raw !== 'string' || !this.isJsonValue(raw)) {
            return [];
        }

        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
            return [];
        }
    }

    private readNumber(source: any, names: string[], fallback: number): number {
        const raw = this.readRaw(source, names);

        if (raw === undefined || raw === null || raw === '') {
            return fallback;
        }

        const value = String(raw)
            .replace('m�', '')
            .replace('jaar', '')
            .trim();

        const normalized = value.includes(',')
            ? value.replace(/\./g, '').replace(',', '.')
            : value.match(/^\d{1,3}(\.\d{3})+$/)
                ? value.replace(/\./g, '')
                : value;

        const result = Number(normalized);

        return Number.isFinite(result) ? result : fallback;
    }

    private readText(source: any, names: string[]): string {
        const value = this.readRaw(source, names);
        return value === undefined || value === null ? '' : String(value);
    }

    private getParameterValue(source: any, names: string[]): string | undefined {
        const value = this.readRaw(source, names);
        return value === undefined || value === null ? undefined : String(value);
    }

    private readRaw(source: any, names: string[]): any {
        const parameters = source?.parameters ?? [];
        const normalizedNames = names.map(x => this.normalizeKey(x));

        for (const name of normalizedNames) {
            const parameter = parameters.find((x: any) =>
                this.normalizeKey(x?.name) === name
            );

            if (parameter) {
                return parameter.value;
            }
        }

        for (const name of normalizedNames) {
            const parameter = parameters.find((x: any) => {
                const key = this.normalizeKey(x?.name);
                return key.includes(name) || name.includes(key);
            });

            if (parameter) {
                return parameter.value;
            }
        }

        for (const name of normalizedNames) {
            const key = Object.keys(source ?? {}).find(x =>
                this.normalizeKey(x) === name
            );

            if (key) {
                return source[key];
            }
        }

        for (const name of normalizedNames) {
            const key = Object.keys(source ?? {}).find(x => {
                const normalizedKey = this.normalizeKey(x);
                return normalizedKey.includes(name) || name.includes(normalizedKey);
            });

            if (key) {
                return source[key];
            }
        }

        return undefined;
    }

    private normalize(value: unknown): string {
        return String(value ?? '').trim().toLowerCase();
    }

    private normalizeKey(value: unknown): string {
        return String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    }

    /**
* get CombinationBuildingFeatureFlag feature flag
*/
    getCombinationBuildingFeatureFlag() {
        this._featureService.isShowCombinationBuildingFeature()
            .pipe(takeUntil(this._onDestroy))
            .subscribe(resp => this.isShowCombinationBuildingFeature = resp);
    }
}
